package dev.lingvo.shared.screenshots.render

import dev.lingvo.shared.screenshots.doc.DesignIssue
import dev.lingvo.shared.screenshots.doc.ImageSource
import dev.lingvo.shared.screenshots.doc.ScreenshotDesign
import dev.lingvo.shared.screenshots.doc.TextContent
import dev.lingvo.shared.screenshots.doc.TypeRole
import dev.lingvo.shared.screenshots.text.FitResult
import dev.lingvo.shared.screenshots.text.FontResolver
import dev.lingvo.shared.screenshots.text.ScriptResolver
import dev.lingvo.shared.screenshots.text.Segmenter

/** Export mode refuses incomplete input; editor mode exposes repairable gaps. */
enum class RenderMode { EDITOR, EXPORT }

/** Callbacks keep rendering independent of asset storage and translation transport. */
class RenderInputs(
    val locale: String, val mode: RenderMode,
    val captions: (content: TextContent, slotIndex: Int) -> String?,
    val images: (source: ImageSource, slotIndex: Int) -> ImageHandle?,
    val measurer: TextMeasurer, val segmenter: Segmenter, val fonts: FontResolver, val scripts: ScriptResolver,
    val templateFamilies: Map<TypeRole, String> = emptyMap(),
)

/** [sourceText] is the caption callback's value; [drawnText] is the drawn lines joined by "\n". */
data class TextReport(
    val layerId: String, val content: TextContent, val sourceText: String,
    val drawnText: String, val fit: FitResult, val font: ResolvedFont,
    /** Schema-3 primary slot layer identity survives a locale-specific Key binding. */
    val primaryCaption: Boolean = false,
)

/** Missing inputs retain layer identity so review can link directly to a repair. */
sealed interface MissingInput {
    /** A missing capture prevents a faithful store screenshot. */
    data class Capture(val layerId: String, val slotIndex: Int) : MissingInput
    /** A missing caption prevents approval of the translated message. */
    data class Caption(val layerId: String, val content: TextContent) : MissingInput
    /** Optional artwork can be exposed as an editor warning. */
    data class Asset(val layerId: String, val assetId: String) : MissingInput
    /** An optional logo remains distinguishable from a missing capture. */
    data class Logo(val layerId: String) : MissingInput
}

/** Exposes provenance without requiring the server to reproduce text shaping. */
data class RenderReport(val texts: List<TextReport>, val missing: List<MissingInput>, val issues: List<DesignIssue>) {
    /** Any refused fit, missing capture or missing caption. EXPORT callers must not upload. */
    val blocking: Boolean
        get() = texts.any { it.fit is FitResult.Refused } || missing.any {
            it is MissingInput.Capture || it is MissingInput.Caption
        }

    /** Primary caption source, hashed verbatim; retained schemas keep their first-slot-caption rule. */
    val slotCaption: String?
        get() = (texts.firstOrNull { it.primaryCaption } ?: texts.firstOrNull { it.content is TextContent.SlotCaption })?.sourceText
}

/** Exports and previews share the same document and surface contract. */
fun interface ShotRenderer {
    /** Returns provenance and fit information for the selected store cell. */
    fun renderSlot(design: ScreenshotDesign, boardId: String, slotId: String, inputs: RenderInputs, surface: Surface): RenderReport
}
